// Minimal, dependency-free XML reader.
//
// Why not `DOMParser`? The IDS parser only needs four members of an XML
// element (localName, textContent, children, getAttribute), but
// `DOMParser` is a browser global: it does not exist in Node, which is
// exactly where the conformance suite and every CI job run. Rather than
// depend on jsdom/happy-dom (a heavy dev-time dependency leaking into a
// zero-dependency library) or ship two code paths with two behaviours,
// the package parses XML itself and behaves identically in every
// runtime. Hosts that prefer the platform parser can still inject one:
// see `DomParserLike` and the `domParser` option of `parseIds`.
//
// Deliberately NOT supported, for safety: DTD internal subsets are
// skipped and custom entity declarations are ignored, so entity
// expansion attacks (billion laughs) and external entity references
// (XXE) cannot happen. Only the five predefined XML entities and
// numeric character references are expanded.

/** The slice of an XML element this package relies on. A native DOM
 *  `Element` satisfies it structurally, so an injected `DOMParser`
 *  works without adaptation. */
export interface XmlElementLike {
  /** Tag name without namespace prefix. */
  readonly localName: string
  /** Concatenated text of the whole subtree. */
  readonly textContent: string | null
  /** Child ELEMENTS only (no text nodes), in document order. */
  readonly children: ArrayLike<XmlElementLike>
  getAttribute(name: string): string | null
}

/** The slice of an XML document this package relies on. */
export interface XmlDocumentLike {
  readonly documentElement: XmlElementLike | null
  /** Present so a native `Document` fits; used to detect the
   *  `<parsererror>` element browsers produce for malformed input. */
  getElementsByTagName?(name: string): ArrayLike<unknown>
}

/** Anything shaped like a `DOMParser`. */
export interface DomParserLike {
  parseFromString(source: string, mimeType: string): XmlDocumentLike
}

/** Thrown when the input is not well-formed XML. */
export class XmlParseError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'XmlParseError'
  }
}

// --- Implementation ----------------------------------------------------

const PREDEFINED: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
}

function decodeEntities(s: string): string {
  if (s.indexOf('&') === -1) return s
  return s.replace(/&(#x?[0-9a-fA-F]+|[A-Za-z_][\w.\-:]*);/g, (all, ref: string) => {
    if (ref.charCodeAt(0) === 35 /* # */) {
      const hex = ref[1] === 'x' || ref[1] === 'X'
      const code = Number.parseInt(hex ? ref.slice(2) : ref.slice(1), hex ? 16 : 10)
      if (!Number.isFinite(code) || code < 0 || code > 0x10ffff) {
        throw new XmlParseError(`invalid character reference "${all}"`)
      }
      return String.fromCodePoint(code)
    }
    const v = PREDEFINED[ref]
    if (v === undefined) {
      throw new XmlParseError(`undefined entity reference "${all}"`)
    }
    return v
  })
}

function localNameOf(qname: string): string {
  const i = qname.indexOf(':')
  return i === -1 ? qname : qname.slice(i + 1)
}

class XmlElement implements XmlElementLike {
  readonly tagName: string
  readonly localName: string
  readonly children: XmlElement[] = []
  /** Text directly owned by this element, interleaved with children.
   *  Only the concatenation matters here, so order across children is
   *  preserved by appending markers into `text` as we go. */
  private readonly parts: Array<string | XmlElement> = []
  private readonly attrs: Map<string, string>

  constructor(tagName: string, attrs: Map<string, string>) {
    this.tagName = tagName
    this.localName = localNameOf(tagName)
    this.attrs = attrs
  }

  appendText(t: string): void {
    this.parts.push(t)
  }

  appendChild(el: XmlElement): void {
    this.parts.push(el)
    this.children.push(el)
  }

  get textContent(): string {
    let out = ''
    for (const p of this.parts) {
      out += typeof p === 'string' ? p : p.textContent
    }
    return out
  }

  /** Attribute lookup by qualified name, falling back to local name so
   *  `getAttribute('cardinality')` also finds `ids:cardinality`. */
  getAttribute(name: string): string | null {
    const direct = this.attrs.get(name)
    if (direct !== undefined) return direct
    for (const [k, v] of this.attrs) {
      if (localNameOf(k) === name) return v
    }
    return null
  }
}

class XmlDocument implements XmlDocumentLike {
  documentElement: XmlElementLike | null = null
}

const NAME_START = /[A-Za-z_:]/
const NAME_CHAR = /[-A-Za-z0-9_:.]/

/** Parses an XML string into a minimal read-only document. Throws
 *  `XmlParseError` when the input is not well-formed. */
export function parseXml(source: string): XmlDocumentLike {
  const doc = new XmlDocument()
  const stack: XmlElement[] = []
  let i = 0
  const n = source.length
  let sawRoot = false

  const fail = (msg: string): never => {
    throw new XmlParseError(`${msg} (at offset ${i})`)
  }

  const readName = (): string => {
    const start = i
    if (i >= n || !NAME_START.test(source[i])) fail('expected a name')
    i++
    while (i < n && NAME_CHAR.test(source[i])) i++
    return source.slice(start, i)
  }

  const skipSpace = (): void => {
    while (i < n && (source[i] === ' ' || source[i] === '\t' || source[i] === '\n' || source[i] === '\r')) {
      i++
    }
  }

  /** Skips `<!DOCTYPE ...>`, including any internal subset in `[...]`.
   *  Nothing inside it is interpreted (see the safety note above). */
  const skipDoctype = (): void => {
    let depth = 0
    while (i < n) {
      const c = source[i]
      if (c === '[') depth++
      else if (c === ']') depth--
      else if (c === '>' && depth <= 0) {
        i++
        return
      }
      i++
    }
    fail('unterminated <!DOCTYPE>')
  }

  const skipUntil = (end: string, what: string): void => {
    const at = source.indexOf(end, i)
    if (at === -1) fail(`unterminated ${what}`)
    i = at + end.length
  }

  while (i < n) {
    if (source[i] !== '<') {
      // Character data.
      const next = source.indexOf('<', i)
      const chunk = source.slice(i, next === -1 ? n : next)
      if (stack.length > 0) {
        stack[stack.length - 1].appendText(decodeEntities(chunk))
      } else if (chunk.trim() !== '') {
        fail('text outside of the root element')
      }
      i = next === -1 ? n : next
      continue
    }

    if (source.startsWith('<!--', i)) {
      i += 4
      skipUntil('-->', 'comment')
      continue
    }
    if (source.startsWith('<![CDATA[', i)) {
      i += 9
      const at = source.indexOf(']]>', i)
      if (at === -1) fail('unterminated CDATA section')
      const text = source.slice(i, at)
      if (stack.length > 0) stack[stack.length - 1].appendText(text)
      i = at + 3
      continue
    }
    if (source.startsWith('<?', i)) {
      i += 2
      skipUntil('?>', 'processing instruction')
      continue
    }
    if (source.startsWith('<!DOCTYPE', i)) {
      i += 9
      skipDoctype()
      continue
    }
    if (source.startsWith('</', i)) {
      i += 2
      const name = readName()
      skipSpace()
      if (source[i] !== '>') fail('malformed closing tag')
      i++
      const open = stack.pop()
      if (!open) fail(`closing tag </${name}> without a matching opening tag`)
      else if (open.tagName !== name) {
        fail(`closing tag </${name}> does not match <${open.tagName}>`)
      }
      continue
    }

    // Opening (or self-closing) tag.
    i++
    const tagName = readName()
    const attrs = new Map<string, string>()
    for (;;) {
      skipSpace()
      if (i >= n) fail('unterminated tag')
      if (source[i] === '>') {
        i++
        break
      }
      if (source.startsWith('/>', i)) {
        i += 2
        break
      }
      const attrName = readName()
      skipSpace()
      if (source[i] !== '=') fail(`attribute "${attrName}" without a value`)
      i++
      skipSpace()
      const quote = source[i]
      if (quote !== '"' && quote !== "'") fail('attribute value is not quoted')
      i++
      const end = source.indexOf(quote, i)
      if (end === -1) fail('unterminated attribute value')
      const rawValue = source.slice(i, end)
      if (rawValue.indexOf('<') !== -1) fail('"<" is not allowed in an attribute value')
      if (attrs.has(attrName)) fail(`duplicate attribute "${attrName}"`)
      attrs.set(attrName, decodeEntities(rawValue))
      i = end + 1
    }
    const selfClosing = source[i - 2] === '/' && source[i - 1] === '>'

    const el = new XmlElement(tagName, attrs)
    const parent = stack[stack.length - 1]
    if (parent) {
      parent.appendChild(el)
    } else {
      if (sawRoot) fail('more than one root element')
      sawRoot = true
      doc.documentElement = el
    }
    if (!selfClosing) stack.push(el)
  }

  if (stack.length > 0) {
    throw new XmlParseError(`unclosed element <${stack[stack.length - 1].tagName}>`)
  }
  if (!sawRoot) throw new XmlParseError('the document has no root element')
  return doc
}

/** Parses with an injected `DOMParser`-like object, normalising the
 *  browser convention of reporting malformed XML as a `<parsererror>`
 *  element into a thrown `XmlParseError`. */
export function parseXmlWith(
  parser: DomParserLike,
  source: string,
): XmlDocumentLike {
  const doc = parser.parseFromString(source, 'application/xml')
  const errs = doc.getElementsByTagName?.('parsererror')
  if (errs && errs.length > 0) {
    throw new XmlParseError('the XML could not be read (malformed)')
  }
  if (!doc.documentElement) {
    throw new XmlParseError('the document has no root element')
  }
  return doc
}
